import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { formatClock, team, type GameMeta, type MomentDecision, type RoomSnapshot } from '@clutch/core';
import { useClient } from '../client';
import type { MomentTrace } from '../api';
import { useRoomStream } from '../room/useRoom';
import { choiceLabel, isProb, pct, replayBadge } from '../room/derive';
import { teamAccent } from '../ui/color';
import { domainEnd, elapsed, periodTicks, probPath, scaler, type ChartBox } from './chart';

const SPEEDS = [1, 5, 30] as const;

export function ControlRoom({ roomId: initialRoom, layout }: { roomId?: string; layout: 'page' | 'side' }) {
  const client = useClient();
  const [roomId, setRoomId] = useState(initialRoom);
  useEffect(() => setRoomId(initialRoom), [initialRoom]);

  const open = (id: string) => {
    setRoomId(id);
    if (layout === 'page') history.replaceState(null, '', `/control?room=${encodeURIComponent(id)}`);
  };

  return (
    <div className={`ctl ctl--${layout}`}>
      <header className="ctl__head">
        <div>
          <p className="ctl__eyebrow">Developer · Clutch control room — not part of the fan app</p>
          <h1 className="ctl__title">{roomId ? 'Room trace' : 'Start a replay room'}</h1>
        </div>
        <span className={`ctl__mode ctl__mode--${client.mode}`}>
          {client.mode === 'fixture' ? 'DEV fixture driver · no model' : 'Server'}
        </span>
      </header>
      <CreateRoom onCreated={open} compact={!!roomId} />
      {roomId && <RoomTrace roomId={roomId} />}
    </div>
  );
}

function CreateRoom({ onCreated, compact }: { onCreated: (roomId: string) => void; compact: boolean }) {
  const client = useClient();
  const [games, setGames] = useState<GameMeta[]>();
  const [gameId, setGameId] = useState<string>();
  const [seq, setSeq] = useState(480);
  const [speed, setSpeed] = useState<number>(1);
  const [err, setErr] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState(!compact);

  useEffect(() => {
    client
      .listGames()
      .then((g) => {
        setGames(g);
        setGameId((cur) => cur ?? g[0]?.gameId);
      })
      .catch(() => setErr('Could not load games from the server.'));
  }, [client]);

  if (compact && !openForm)
    return (
      <button className="linkbtn ctl__new" onClick={() => setOpenForm(true)}>
        + New room
      </button>
    );

  const create = async () => {
    if (!gameId) return;
    setBusy(true);
    setErr(undefined);
    try {
      const { roomId } = await client.createRoom({ gameId, startAtSeq: seq, speed });
      onCreated(roomId);
      setOpenForm(false);
    } catch {
      setErr('Room creation failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ctl__card ctl__create">
      <label className="ctl__field">
        <span>Game (real 2025-26 play-by-play)</span>
        <select value={gameId ?? ''} onChange={(e) => setGameId(e.target.value)} disabled={!games}>
          {games?.map((g) => (
            <option key={g.gameId} value={g.gameId}>
              {g.away.tricode} @ {g.home.tricode} · {g.date} · {g.gameId}
            </option>
          ))}
        </select>
      </label>
      <div className="ctl__row">
        <label className="ctl__field">
          <span>Start at event seq</span>
          <input type="number" min={0} value={seq} onChange={(e) => setSeq(Math.max(0, Number(e.target.value) || 0))} />
        </label>
        <label className="ctl__field">
          <span>Speed</span>
          <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
            {[1, 5, 30, 60].map((s) => (
              <option key={s} value={s}>
                {s}×
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn--primary" onClick={create} disabled={busy || !gameId}>
          {busy ? 'Creating…' : 'Create room'}
        </button>
      </div>
      {err && <p className="alert">{err}</p>}
    </section>
  );
}

function RoomTrace({ roomId }: { roomId: string }) {
  const client = useClient();
  const [trace, setTrace] = useState<MomentTrace>();
  const [copied, setCopied] = useState(false);

  const refreshMoments = useCallback(() => {
    client
      .moments(roomId)
      .then(setTrace)
      .catch(() => undefined);
  }, [client, roomId]);

  const { snapshot: s, status } = useRoomStream(roomId, (e) => {
    if (e.kind === 'moment') refreshMoments();
  });
  useEffect(() => {
    refreshMoments();
    const t = setInterval(refreshMoments, 5000);
    return () => clearInterval(t);
  }, [refreshMoments]);

  const link = `${location.origin}/room/${encodeURIComponent(roomId)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Room link', link);
    }
  };
  const ctl = (cmd: Parameters<typeof client.control>[1]) => client.control(roomId, cmd).catch(() => undefined);

  const moments = traceRows(trace);
  if (!s) return <p className="muted ctl__card">Connecting to room {roomId}… ({status})</p>;
  const st = s.state;
  const playing = s.replay.status === 'playing';
  const live = s.feed?.mode === 'live';
  const style = { '--home': teamAccent(s.meta.home.tricode), '--away': teamAccent(s.meta.away.tricode) } as CSSProperties;

  return (
    <div className="ctl__trace" style={style}>
      <section className="ctl__card ctl__live">
        <div className="ctl__score">
          <span className="ctl__team" style={{ color: 'var(--away)' }}>
            {s.meta.away.tricode}
          </span>
          <span className="ctl__num">{st.awayScore}</span>
          <span className="ctl__clock">{st.status === 'final' ? 'FINAL' : formatClock(st.period, st.clockSec)}</span>
          <span className="ctl__num">{st.homeScore}</span>
          <span className="ctl__team" style={{ color: 'var(--home)' }}>
            {s.meta.home.tricode}
          </span>
        </div>
        <p className="ctl__meta">
          {live
            ? `LIVE · ${s.feed?.source === 'espn_public' ? 'ESPN feed' : 'live feed'}${typeof s.feed?.lagMs === 'number' ? ` · ${Math.round(s.feed.lagMs / 1000)}s lag` : ''}${s.feed?.message ? ` · ${s.feed.message}` : ''} · cursor seq ${s.replay.cursorSeq}`
            : `${replayBadge(s.replay.speed)} · ${s.replay.status} · cursor seq ${s.replay.cursorSeq} / ${s.replay.totalEvents - 1}`}
        </p>
        <div className="ctl__controls">
          {!live && (
            <button className="btn btn--small" onClick={() => ctl({ action: playing ? 'pause' : 'play' })}>
              {playing ? 'Pause' : 'Play'}
            </button>
          )}
          {!live && SPEEDS.map((sp) => (
            <button
              key={sp}
              className={`btn btn--small${s.replay.speed === sp ? ' is-on' : ''}`}
              onClick={() => ctl({ action: 'speed', speed: sp })}
              aria-pressed={s.replay.speed === sp}
            >
              {sp}×
            </button>
          ))}
          <button className="btn btn--small btn--ghost" onClick={copy}>
            {copied ? 'Copied' : 'Copy room link'}
          </button>
        </div>
        <p className="ctl__link" title={link}>
          {link}
        </p>
      </section>

      <section className="ctl__card">
        <h2 className="ctl__h2">
          Win probability — {team(s.meta.home.tricode).name} (home)
          <span className="ctl__sub">
            {s.probability.length
              ? `model ${s.probability[s.probability.length - 1]!.modelVersion} · ${s.probability.length} points from real events`
              : 'no model points yet'}
          </span>
        </h2>
        <ProbChart s={s} moments={trace?.moments ?? s.moments} />
      </section>

      <section className="ctl__card">
        <h2 className="ctl__h2">
          Moment decisions <span className="ctl__sub">why Clutch did (or didn’t) ping fans</span>
        </h2>
        {trace && (
          <p className="ctl__meta">
            detector {trace.detectorVersion} · model {trace.modelVersion} · {trace.observed} plays evaluated · {trace.fired}{' '}
            fired
          </p>
        )}
        {moments.length === 0 ? (
          <p className="muted">No decisions yet — the detector logs one per evaluated play.</p>
        ) : (
          <ol className="ctl__moments">
            {moments.slice(0, 40).map((m) => (
                <li key={m.momentId} className={m.isMoment ? 'is-fired' : ''}>
                  <span className="ctl__mclock">{formatClock(m.period, m.clockSec)}</span>
                  <span className={`ctl__mbadge${m.isMoment ? ' is-fired' : ''}`}>{m.isMoment ? 'FIRED' : 'held'}</span>
                  <span className="ctl__mscore">{m.score.toFixed(2)}</span>
                  <span className="ctl__mreasons">{m.reasons.join(' · ')}</span>
                  <span className="ctl__mver">
                    {m.detectorVersion} · seq {m.seq}
                  </span>
                </li>
              ))}
          </ol>
        )}
      </section>

      <section className="ctl__card">
        <h2 className="ctl__h2">
          Room <span className="ctl__sub">presence and server-confirmed locks</span>
        </h2>
        {s.call && (
          <p className="ctl__callq">
            {s.call.question} · {s.call.status} · cutoff {formatClock(s.call.cutoff.period, s.call.cutoff.clockSec)}
          </p>
        )}
        <table className="ctl__table">
          <thead>
            <tr>
              <th>Fan</th>
              <th>Status</th>
              <th>Call</th>
              <th>Locked at</th>
              <th>Chance</th>
            </tr>
          </thead>
          <tbody>
            {s.presence.map((p) => {
              const l = s.locks.find((x) => x.fanId === p.fanId);
              const r = s.resolution?.results.find((x) => x.fanId === p.fanId);
              return (
                <tr key={p.fanId}>
                  <td>{p.displayName}</td>
                  <td>{p.online ? 'online' : 'away'}</td>
                  <td>
                    {l && s.call ? choiceLabel(s.call, l.choice) : '—'}
                    {r ? (r.correct ? ' ✓' : ' ✗') : ''}
                  </td>
                  <td>{l ? `${formatClock(l.lockedAt.period, l.lockedAt.clockSec)} · seq ${l.lockedAtSeq}` : '—'}</td>
                  <td>{l && isProb(l.pChoiceAtLock) ? `${pct(l.pChoiceAtLock)}%` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

const BOX: ChartBox = { w: 640, h: 220, padL: 40, padR: 12, padT: 12, padB: 26 };

function ProbChart({ s, moments }: { s: RoomSnapshot; moments: MomentDecision[] }) {
  const xEnd = domainEnd(s.state.period);
  const sc = scaler(BOX, xEnd);
  const path = probPath(s.probability, BOX, xEnd);
  const fired = moments.filter((m) => m.isMoment);
  const last = s.probability[s.probability.length - 1];
  const cutoff = s.call?.cutoff;
  return (
    <svg className="chart" viewBox={`0 0 ${BOX.w} ${BOX.h}`} role="img" aria-label="Home win probability over game time">
      {[0, 0.25, 0.5, 0.75, 1].map((p) => (
        <g key={p}>
          <line x1={BOX.padL} x2={BOX.w - BOX.padR} y1={sc.y(p)} y2={sc.y(p)} className={p === 0.5 ? 'chart__mid' : 'chart__grid'} />
          <text x={BOX.padL - 6} y={sc.y(p) + 4} className="chart__ylab" textAnchor="end">
            {Math.round(p * 100)}%
          </text>
        </g>
      ))}
      {periodTicks(xEnd).map((t) => (
        <g key={t.label}>
          <line x1={sc.x(t.at)} x2={sc.x(t.at)} y1={BOX.padT} y2={BOX.h - BOX.padB} className="chart__grid" />
          <text x={sc.x(t.at) + 4} y={BOX.h - 8} className="chart__xlab">
            {t.label}
          </text>
        </g>
      ))}
      {cutoff && (
        <line
          x1={sc.x(elapsed(cutoff.period, cutoff.clockSec))}
          x2={sc.x(elapsed(cutoff.period, cutoff.clockSec))}
          y1={BOX.padT}
          y2={BOX.h - BOX.padB}
          className="chart__cutoff"
        />
      )}
      {fired.map((m) => (
        <line
          key={m.momentId}
          x1={sc.x(elapsed(m.period, m.clockSec))}
          x2={sc.x(elapsed(m.period, m.clockSec))}
          y1={BOX.padT}
          y2={BOX.h - BOX.padB}
          className="chart__moment"
        />
      ))}
      {path && <path d={path} className="chart__line" />}
      {last && Number.isFinite(last.pHome) && (
        <circle cx={sc.x(elapsed(last.period, last.clockSec))} cy={sc.y(last.pHome)} r={4} className="chart__head" />
      )}
      {!path && (
        <text x={BOX.w / 2} y={BOX.h / 2} textAnchor="middle" className="chart__empty">
          Waiting for model probability events
        </text>
      )}
    </svg>
  );
}

/** Fired moments + recent decisions, newest first, de-duplicated. */
export function traceRows(trace: MomentTrace | undefined): MomentDecision[] {
  if (!trace) return [];
  const byId = new Map<string, MomentDecision>();
  for (const m of [...trace.recent, ...trace.moments]) byId.set(m.momentId, m);
  return [...byId.values()].sort((a, b) => b.seq - a.seq);
}
